import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { ApiService } from '@app/shared/services/api/api.service';
import { ExcelService, Excel } from '@app/shared/services/api/excel.service';
import { MeterDistributor } from '@app/shared/services/api/meter-data.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { saveFile } from '@app/shared/utility';
import { cssVariables } from '@env/css-vars';
import { keys, map as _map, values } from 'lodash';
import { Observable, of } from 'rxjs';
import { finalize, map, mergeMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MeterDistributorChangeService extends ApiService {
  constructor(
    private excelService: ExcelService,
    private userMunicipality: UserMunicipalitiesService,
    private localNumberPipe: LocalNumberPipe,
    private _loader: OverlayLoaderService,
    private _matDialog: MatDialog,
    private _httpClient: HttpClient
  ) {
    super(_httpClient);
  }

  downloadExcel = (isMunicipalityDiscount: boolean) => {
    this._loader.isLoading = true;
    let fileName: string = '';
    let excelFileName: string = '';
    if (isMunicipalityDiscount) {
      fileName = 'Kommunalrabatt';
      excelFileName = this.getMunicipalDiscountExcelFileName();
    } else {
      fileName = 'Lieferantenwechsel';
      excelFileName = this.getSupplierChangeExcelFileName();
    }
    return this.getMeterDistributorChange(this.userMunicipality.currentMunicipality.key).pipe(
      map((meter: MeterDistributor[]) => {
        if (meter.length === 0) {
          throw Error('No value');
        }
        return _map(meter, (data) => this.mapMeterData(data, !isMunicipalityDiscount));
      }),
      mergeMap((meterData) => this.generateExcel(fileName, meterData)),
      map((blobData: Blob) => saveFile(blobData, excelFileName)),
      tap({
        error: () => {
          this.showErrorMessage();
        },
      }),
      finalize(() => (this._loader.isLoading = false))
    );
  };

  getMeterDistributorChange = (municipalityKey: string): Observable<MeterDistributor[]> =>
    this._httpClient.get<MeterDistributor[]>(
      this.endpoint(`socrates/meters/api/views/ViewMeterDistributorChange?$filter=municipalitykey eq '${municipalityKey}'`),
      {}
    );

  generateExcel = (fileName: string, data: any): Observable<Blob> =>
    of(this.mapExcelData(fileName, data)).pipe(
      mergeMap((excelData: Excel) => this.excelService.generateExcel(excelData)),
      map(this.formatToBlob)
    );

  mapExcelData = (fileName: string, data: any): Excel => ({
    fileName: `${fileName}.xlsx`,
    sheetName: fileName,
    headers: keys(data[0]),
    data: _map(data, values),
    protectedColumns: [],
    templateCode: '',
    headerBgColor: cssVariables['--ep-primary-color'],
    secretPhrase: '',
  });

  formatToBlob = (xlsx: any) =>
    new Blob([xlsx], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

  private getSupplierChangeExcelFileName = () => `${this.formatDateToYYYYMMDDString(new Date(Date.now()))}_Zählerübersicht_für_Lieferantenwechsel.xlsx`;

  private getMunicipalDiscountExcelFileName = () =>
    `${this.formatDateToYYYYMMDDString(new Date(Date.now()))}_Kommunalrabatt_${this.userMunicipality.currentMunicipality.name}.xlsx`;

  private formatDateToYYYYMMDDString = (date: Date) => `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;

  mapMeterData = (data: MeterDistributor, extended: boolean) => {
    const formattedData = {
      Marktlokation: data.marketLocationKey,
      Messlokation: data.measurementLocationKey,
      Standardlastprofil: data.profile1External,
      Zählernummer: data.meterNumber,
      Zählerart: data.billingCategory,
      Tarifart: data.registerType,
      Sparte: data.energyTypeName,
      AGS: data.municipalityKey,
      PLZ: data.zipCode,
      Ort: data.municipality,
      Ortsteil: data.district,
      Strasse: data.street,
      Hausnummer: data.houseNumber,
      Objekttyp: data.objectType,
      'Objekt Name': data.objectName, // eslint-disable-line
      Netzebene: data.gridLevelDescription,
      Kommunalrabatt: data.municipalityDiscount,
      Messstellenbetreiber: data.meterOperator,
    };

    if (extended) {
      return {
        ...formattedData,
        'aktuell letzter Zählerstand': data.count ? this.localNumberPipe.transform(data.count) : data.count, // eslint-disable-line
        Jahresverbrauch: data.amountYear ? this.localNumberPipe.transform(data.amountYear) : data.amountYear,
        'Kalenderjahr ': data.calendarYear, // eslint-disable-line
      };
    }

    return formattedData;
  };

  private showErrorMessage = (): void => {
    this._matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Liste kann nicht heruntergeladen werden, da noch keine Daten existieren',
        descriptionFaClass: 'fa-exclamation-triangle fa-2x',
        orientation: 'row',
        alignment: 'start',
      },
    });
  };
}
